import { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { User, Upload, Camera, Check, Loader2 } from 'lucide-react';
import { rawFetch } from '../../services/api';
import { generateRandomAvatar } from './avatarGenerator';

interface ProfileSetupProps {
  userReference: string;
  currentName?: string;
  currentImageUrl?: string | null;
  mode?: 'setup' | 'edit';
  onComplete: () => void;
  onCancel?: () => void;
}

export default function ProfileSetup({ userReference, currentName, currentImageUrl, mode = 'setup', onComplete, onCancel }: ProfileSetupProps) {
  const [name, setName] = useState(currentName || '');
  const [imagePreview, setImagePreview] = useState<string | null>(currentImageUrl || null);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [cropMode, setCropMode] = useState(false);
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
        setImagePreview(ev.target?.result as string);
        setCropMode(true);
        setCropOffset({ x: 0, y: 0 });
        setError('');
      };
      img.src = ev.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const cropAndEncode = useCallback(async () => {
    if (!imagePreview || !canvasRef.current) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    return new Promise<Blob>((resolve, reject) => {
      img.onload = () => {
        const canvas = canvasRef.current!;
        const size = Math.min(img.width, img.height);
        const outputSize = 512; // Fixed output size
        canvas.width = outputSize;
        canvas.height = outputSize;

        const ctx = canvas.getContext('2d')!;
        
        // Calculate crop area from center + offset
        const centerX = img.width / 2 + cropOffset.x * (img.width / 2);
        const centerY = img.height / 2 + cropOffset.y * (img.height / 2);
        const halfSize = size / 2;
        
        const sx = Math.max(0, Math.min(centerX - halfSize, img.width - size));
        const sy = Math.max(0, Math.min(centerY - halfSize, img.height - size));

        ctx.drawImage(img, sx, sy, size, size, 0, 0, outputSize, outputSize);

        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to encode image'));
          },
          'image/webp',
          0.85
        );
      };
      img.onerror = reject;
      img.src = imagePreview;
    });
  }, [imagePreview, cropOffset]);

  const handleCropConfirm = async () => {
    try {
      const blob = await cropAndEncode();
      if (blob) {
        setCroppedBlob(blob);
        // Create preview from blob
        const url = URL.createObjectURL(blob);
        setImagePreview(url);
        setCropMode(false);
      }
    } catch {
      setError('Failed to process image');
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!croppedBlob) return null;

    setIsUploading(true);
    try {
      // 1. Get presigned URL
      const presignRes = await rawFetch(
        `/api/public/fanzone/${userReference}/profile-image/presign`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            contentType: 'image/webp',
            fileSize: croppedBlob.size,
          }),
        }
      );

      if (!presignRes.ok) {
        const err = await presignRes.json() as any;
        throw new Error(err.error || 'Failed to get upload URL');
      }

      const { uploadUrl, path } = await presignRes.json() as { uploadUrl: string; path: string };

      // 2. Upload to R2
      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/webp' },
        body: croppedBlob,
      });

      if (!uploadRes.ok) throw new Error('Upload failed');

      // 3. Confirm
      const confirmRes = await rawFetch(
        `/api/public/fanzone/${userReference}/profile-image/confirm`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ path }),
        }
      );

      if (!confirmRes.ok) {
        const err = await confirmRes.json() as any;
        throw new Error(err.error || 'Failed to confirm upload');
      }

      const data = await confirmRes.json() as { data: { profileImageUrl: string } };
      return data.data.profileImageUrl;
    } catch (err: any) {
      setError(err.message || 'Image upload failed');
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (name.trim().length > 20) {
      setError('Display name must be 20 characters or less');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      // Upload image if selected
      if (croppedBlob) {
        await uploadImage();
      }

      // Update profile name + activate
      const res = await rawFetch(
        `/api/public/fanzone/${userReference}/profile`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: name.trim() }),
        }
      );

      if (!res.ok) {
        const err = await res.json() as any;
        throw new Error(err.error || 'Failed to save profile');
      }

      onComplete();
    } catch (err: any) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  const defaultAvatar = generateRandomAvatar(userReference);

  return (
    <div className="min-h-screen bg-deep-charcoal flex items-center justify-center px-4 py-8">
      {/* Ambient glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-electric-cyan/5 rounded-full blur-[120px]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/fans-audio-logo.png" alt="fans.audio" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">{mode === 'edit' ? 'Edit Profile' : 'Set Up Your Profile'}</h1>
          <p className="text-sm text-text-secondary mt-2 font-mono tracking-wide">
            {mode === 'edit' ? 'Update your name or profile picture' : 'Complete your fan profile to enter your zone'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Image */}
          <div className="flex flex-col items-center">
            <div className="relative group">
              <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-white/10 bg-[#1A1A1A]">
                {imagePreview && !cropMode ? (
                  <img src={imagePreview} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <img src={defaultAvatar} alt="Default avatar" className="w-full h-full object-cover" />
                )}
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 p-2.5 rounded-full bg-electric-cyan text-deep-charcoal hover:bg-electric-cyan/80 transition-colors shadow-lg"
              >
                <Camera className="w-4 h-4" />
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Crop Modal */}
          {cropMode && imagePreview && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4"
            >
              <p className="text-sm text-white font-medium mb-3">Crop your image</p>
              <div className="relative w-full aspect-square overflow-hidden rounded-lg bg-black">
                <img
                  ref={imageRef}
                  src={imagePreview}
                  alt="Crop preview"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{
                    objectPosition: `${50 + cropOffset.x * 50}% ${50 + cropOffset.y * 50}%`,
                  }}
                />
                {/* Crop overlay */}
                <div className="absolute inset-0 border-2 border-electric-cyan/50 rounded-lg pointer-events-none" />
              </div>

              {/* Offset controls */}
              {(imageDimensions.width !== imageDimensions.height) && (
                <div className="mt-3 space-y-2">
                  {imageDimensions.width > imageDimensions.height && (
                    <div>
                      <label className="text-xs text-text-muted font-mono">Horizontal Position</label>
                      <input
                        type="range"
                        min="-1"
                        max="1"
                        step="0.01"
                        value={cropOffset.x}
                        onChange={(e) => setCropOffset(prev => ({ ...prev, x: parseFloat(e.target.value) }))}
                        className="w-full accent-electric-cyan"
                      />
                    </div>
                  )}
                  {imageDimensions.height > imageDimensions.width && (
                    <div>
                      <label className="text-xs text-text-muted font-mono">Vertical Position</label>
                      <input
                        type="range"
                        min="-1"
                        max="1"
                        step="0.01"
                        value={cropOffset.y}
                        onChange={(e) => setCropOffset(prev => ({ ...prev, y: parseFloat(e.target.value) }))}
                        className="w-full accent-electric-cyan"
                      />
                    </div>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={handleCropConfirm}
                className="mt-3 w-full py-2 px-4 rounded-lg bg-electric-cyan text-deep-charcoal font-medium text-sm hover:bg-electric-cyan/80 transition-colors flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                Confirm Crop
              </button>
            </motion.div>
          )}

          {/* Name Input */}
          <div>
            <label className="block text-xs text-text-muted font-mono uppercase tracking-wide mb-2">
              Display Name <span className="text-electric-cyan">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 20))}
                placeholder="Enter your name"
                maxLength={20}
                required
                className="w-full pl-10 pr-4 py-3 bg-[#1A1A1A] border border-white/10 rounded-lg text-white placeholder:text-text-muted focus:outline-none focus:border-electric-cyan/50 focus:ring-1 focus:ring-electric-cyan/20 transition-all"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted font-mono">{name.length}/20</span>
            </div>
          </div>

          {/* Error */}
          {error && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-sm text-red-400 text-center"
            >
              {error}
            </motion.p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isSaving || isUploading || !name.trim()}
            className="w-full py-3.5 px-6 rounded-lg bg-electric-cyan text-deep-charcoal font-bold text-sm hover:bg-electric-cyan/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {(isSaving || isUploading) ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {isUploading ? 'Uploading Image...' : 'Saving...'}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                {mode === 'edit' ? 'Save Changes' : 'Enter My Collection'}
              </>
            )}
          </button>

          {mode === 'edit' && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="w-full py-3 px-6 rounded-lg border border-white/10 text-text-secondary text-sm font-medium hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
          )}
        </form>
      </motion.div>

      {/* Hidden canvas for image processing */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
